<script setup lang="ts">
import { onMounted, ref } from 'vue';
import moment from 'moment';
import { useRoute } from 'vue-router';
import Print from '.././components/icons/PrintIcon.vue';
import Reload from '.././components/icons/ReloadIcon.vue';
import { PaymentApi } from '@/dashboard/api/payment';
import { FullTransaction } from '@/dashboard/types/transaction';

const route = useRoute();
const transactions = ref<FullTransaction[]>([]);
const loading = ref(false);

onMounted(async () => {
  await loadData();
});

async function loadData() {
  try {
    loading.value = true;
    const workspaceId = Number(route.params.workspace_id);
    const response = await PaymentApi.getWorkspaceTransactions(workspaceId);
    transactions.value = response.transactions;
    loading.value = false;
  } catch (e) {
    loading.value = false;
  }
}

function getString(transaction: FullTransaction): string {
  let str = `${moment(transaction.billing_period.starts_at).format('D.M.YYYY')}`;
  str += ` / ${transaction.details.currency_code} ${(+transaction.details.total / 100).toFixed(2)}`;

  if (transaction.items.length > 0) {
    str += ` / ${transaction.items[0].price.description}`;
  }

  if (transaction.status === 'past_due') {
    str += ` (Check your credit card!) `;
  } else if (transaction.status === 'canceled') {
    str += ` (Canceled) `;
  }

  return str;
}
</script>

<template>
  <div class="flex-1 px-xs pt-[56px] laptop:pt-[95px]">
    <div class="mx-auto flex max-w-[1197px] flex-col gap-y-s pb-[56px]">
      <div class="mb-s flex h-[48px] items-center rounded-6 bg-white pl-s dark:bg-gray-950 max-tablet:mb-xs">
        <span class="mr-xs stroke-black dark:stroke-gray-500">
          <svg width="5" height="8" viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 1L1 4L4 7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <span class="text-caption-s pb-[2px] text-gray-600 dark:text-gray-500">
          <router-link to="/settings" class="underline underline-offset-[2px]">Account</router-link>
          <span> / Billing history</span>
        </span>
      </div>

      <div class="mb-xxl rounded-16 bg-white px-[40px] pb-[40px] pt-[26px] dark:bg-gray-950 max-tablet:px-m max-tablet:pt-l">
        <h5 class="mb-[41px] text-black dark:text-gray-500 max-tablet:mb-s">Billing history</h5>

        <div v-for="item in transactions" :key="item.id" class="mb-[10px] flex flex-col gap-[10px] px-[6px] max-tablet:px-zero">
          <div class="flex items-center justify-between py-[1px]">
            <div class="flex items-center gap-xs">
              <i v-if="item.status === 'canceled'" class="m-icon-not-ok-round-saved"></i>
              <i v-else-if="item.status === 'past_due'" class="m-icon-note-round-saved"></i>
              <i v-else class="m-icon-ok-round-saved"></i>
              <span
                class="text-buttons flex flex-1 text-black dark:text-gray-500"
                :class="{ 'text-red dark:text-mid-red': item.status === 'canceled' || item.status === 'past_due' }"
              >
                {{ getString(item) }}
              </span>
            </div>
            <div>
              <button
                v-if="item.status === 'past_due'"
                class="tppr-button__animation rounded-[6px] hover:shadow-s-hover dark:hover:bg-gray-750"
                @click="loadData"
              >
                <reload class="stroke-gray-500"></reload>
              </button>

              <a
                v-if="item.origin !== 'subscription_payment_method_change'"
                :href="`/user/open_transaction_pdf?id=${item.id}`"
                target="_blank"
                class="tppr-button__animation flex rounded-[6px] hover:shadow-s-hover dark:hover:bg-gray-750"
              >
                <print class="stroke-gray-500"></print>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="loading" class="pointer-events-none fixed bottom-s left-[0px] z-[1002] flex w-full justify-center">
    <div class="tppr-banner pointer-events-auto rounded-8 bg-white dark:bg-gray-900">
      <div class="tppr-banner__content h-xxl">
        <span class="mr-[12px] text-[16px]">🕐</span>
        <span class="text-caption-s mr-s text-black dark:text-white">Loading</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
